import type { OpenApiClient, OpenApiClientOptions } from '@zyno-io/openapi-client-codegen';

import { uxrr } from '@zyno-io/uxrr-client';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { Auth } from '@/shared/services/auth.service';
import { SSEService } from '@/shared/services/sse';

import { client as apiAuthClient } from '../openapi-clients-generated/auth/client.gen';
import { client as apiHostingClient } from '../openapi-clients-generated/hosting/client.gen';
import { client as apiMspClient } from '../openapi-clients-generated/msp/client.gen';
import { client as apiRmmClient } from '../openapi-clients-generated/rmm/client.gen';
import { client as apiSalesClient } from '../openapi-clients-generated/sales/client.gen';
import { client as apiSuiteClient } from '../openapi-clients-generated/suite/client.gen';
import { client as apiTalkSoftphoneClient } from '../openapi-clients-generated/talk-softphone/client.gen';
import { client as apiTalkClient } from '../openapi-clients-generated/talk/client.gen';
import { client as apiThreadsClient } from '../openapi-clients-generated/threads/client.gen';
import { configureZynoOpenApiClient, register401Handler } from './helpers';

export function configureApiClients() {
    // On a 401 from any client, renew the browser JWT once or eject the native session. Registered
    // here so the shared client helper carries no static dependency on MobileBridge / @/global.
    register401Handler(rejectedJwt => {
        if (MobileBridge.isWithinZynoSuiteMobile) {
            MobileBridge.logout();
            return;
        }
        void Auth.recoverUnauthorizedJwt(rejectedJwt);
    });

    apiAuthClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_AUTH_API_URL
    });
    configureApiClient('auth', apiAuthClient, getAuthApiHeadersForRequest);

    apiSuiteClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_SUITE_API_URL
    });
    configureApiClient('suite', apiSuiteClient);

    apiSalesClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_SALES_API_URL
    });
    configureApiClient('sales', apiSalesClient);

    apiTalkClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_TALK_API_URL
    });
    configureApiClient('talk', apiTalkClient);

    apiTalkSoftphoneClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_TALK_SOFTPHONE_API_URL
    });
    configureApiClient('talk-softphone', apiTalkSoftphoneClient);

    apiRmmClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_RMM_API_URL
    });
    configureApiClient('rmm', apiRmmClient);

    apiHostingClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_HOSTING_API_URL
    });
    configureApiClient('hosting', apiHostingClient);

    apiThreadsClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_THREADS_API_URL
    });
    configureApiClient('threads', apiThreadsClient);

    apiMspClient.setConfig({
        baseUrl: import.meta.env.VITE_APP_MSP_API_URL
    });
    configureApiClient('msp', apiMspClient);
}

function configureApiClient(name: string, apiClient: OpenApiClient, apiHeadersFn?: OpenApiClientOptions['headers']) {
    let shouldUseDevAuthz = false;
    // oxlint-disable-next-line typescript/no-explicit-any
    if ((apiClient as any).getConfig().baseUrl?.startsWith('http://') && !MobileBridge.isWithinZynoSuiteMobile) {
        shouldUseDevAuthz = true;
    }
    configureZynoOpenApiClient(name, apiClient, {
        headers: apiHeadersFn ?? (shouldUseDevAuthz ? getApiHeadersForDevRequest : getApiHeadersForRequest)
    });
}

async function getAuthApiHeadersForRequest(request?: Request): Promise<Record<string, string>> {
    return getApiHeadersForRequest(request, true);
}

async function getApiHeadersForRequest(request?: Request, isAuthRequest = false): Promise<Record<string, string>> {
    return getApiHeaders(
        request ? { method: request.method, url: request.url, headers: Object.fromEntries(request.headers.entries()) } : undefined,
        isAuthRequest
    );
}

async function getApiHeadersForDevRequest(request: Request) {
    const baseHeaders = Object.fromEntries(request.headers.entries());

    const authzHeaders = await getApiHeaders({
        method: request.method,
        url: request.url,
        headers: baseHeaders
    });
    const authResponse = await fetch(`${import.meta.env.VITE_APP_AUTH_API_URL}/authz`, {
        headers: {
            ...authzHeaders,
            'x-content-type': baseHeaders['content-type'] || '',
            'x-original-url': request.url,
            'x-original-method': request.method
        }
    });
    if (!authResponse.ok) throw new Error(`Failed to fetch authz headers: ${authResponse.status} ${authResponse.statusText}`);

    const authHeaders = Object.fromEntries([...authResponse.headers.entries()].map(([key, value]) => [key.toLowerCase(), value as string]));
    for (const key in authHeaders) {
        if (key.startsWith('x-zs-')) {
            baseHeaders[key] = authHeaders[key]!;
        }
    }

    const workstationHeaders: Record<string, string> = import.meta.env.VITE_APP_AUTH_WID ? { 'x-zs-wid': import.meta.env.VITE_APP_AUTH_WID } : {};

    return {
        ...baseHeaders,
        ...workstationHeaders,
        Authorization: undefined,
        'x-zs-tz': authzHeaders['x-zs-tz'],
        'x-zs-scid': authzHeaders['x-zs-scid'],
        'x-uxrr-sid': authzHeaders['x-uxrr-sid']
    };
}

export async function getApiHeaders(
    request?: { method: string; url: string; headers: Record<string, string> },
    isAuthRequest = false
): Promise<Record<string, string>> {
    const baseHeaders: Record<string, string> = {
        'x-zs-tz': Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(SSEService.connId && { 'x-zs-scid': SSEService.connId }),
        ...(uxrr.sessionId && { 'x-uxrr-sid': uxrr.sessionId })
    };

    if (request && MobileBridge.isWithinZynoSuiteMobile) {
        const { headers } = await MobileBridge.sendRequest('getHeadersForRequest', { ...request, isAuthRequest });
        return { ...baseHeaders, ...headers };
    }

    // Renewal belongs to the original sign-in, independently of this tab's tenant/account
    // masquerade. A removed target membership must not make the browser credential unrenewable.
    const isSessionRenewal = isAuthRequest && !!request && new URL(request.url).pathname === '/login/renew';
    const renewalAuthorization = isSessionRenewal ? (request?.headers['authorization'] ?? request?.headers['Authorization']) : undefined;
    const authHeaders: Record<string, string> = renewalAuthorization
        ? { Authorization: renewalAuthorization }
        : Auth.activeJwt
          ? { Authorization: `Bearer ${Auth.activeJwt}` }
          : {};
    const masqTid = request?.headers['x-zs-masq-tid'];

    // An explicit per-request x-zs-tid pins the call to one tenant instead of whichever the session
    // happens to be on. /cli-login needs this: a scoped token is bound to its tenant permanently, so
    // the tenant the user picked on the consent page has to win over Auth.tenantId rather than
    // silently following it. The masquerade branches below derive both headers themselves and are
    // deliberately left alone.
    const explicitTid = request?.headers['x-zs-tid'];
    const activeTid = explicitTid ?? Auth.tenantId.value;

    const tenantHeaders: { 'x-zs-tid'?: string; 'x-zs-masq-tid'?: string } = isSessionRenewal
        ? { 'x-zs-tid': Auth.impersonatorTenantId.value ?? Auth.tenantId.value! }
        : masqTid
          ? {
                'x-zs-tid': Auth.impersonatorTenantId.value ?? Auth.tenantId.value!,
                'x-zs-masq-tid': masqTid
            }
          : Auth.impersonatorTenantId.value
            ? {
                  'x-zs-tid': Auth.impersonatorTenantId.value,
                  'x-zs-masq-tid': Auth.tenantId.value!
              }
            : activeTid
              ? {
                    'x-zs-tid': activeTid
                }
              : {};

    const accountHeaders = !isSessionRenewal &&
        Auth.impersonatorUserId.value && {
            'x-zs-masq-aid': Auth.userId.value!
        };

    return {
        ...baseHeaders,
        ...authHeaders,
        ...tenantHeaders,
        ...accountHeaders
    };
}
